var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AssetType, Tenants } from './types';
import { deleteUpload, hideUnhide } from '../services/UploadsMetadataService';
import { action, computed, makeObservable, observable, when } from 'mobx';
import { SHERBERT_BASE_URL, SHERBERT_URL } from '../utilities/constants';
import { fetchAssetIdFromSherbertUrl } from '../utilities/common';
import { deleteAsset, getPageImageInfo, getVariantMetadataWithPolling, hideAsset, presignAsset, unhideAsset, AssetStatusType, AssetErrorMessage, } from '@design-stack-ct/assets-sdk';
import { assetDataFromUMS } from '../type/assetData';
import { VariantType } from '../type/variantsType';
/**
 * An asset object for Vista use cases.
 */
export class VistaAsset {
    constructor(data, assetType, authProvider, signature, callbackFunc) {
        Object.defineProperty(this, "assetType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "status", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { type: AssetStatusType.Unprepared }
        });
        Object.defineProperty(this, "_signature", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_callbackFunc", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_pageImageInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "authProvider", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        makeObservable(this);
        this.authProvider = authProvider;
        this.assetType = assetType;
        this._data = data;
        this._signature = signature;
        this._callbackFunc = callbackFunc;
        when(() => this.status.type === AssetStatusType.Prepared, () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            try {
                if (!this.isDamAsset()) {
                    if (((_c = (_b = (_a = this.data) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.image) === null || _c === void 0 ? void 0 : _c.format) !== 'pdf') {
                        const imageInfoUMS = {
                            width: VistaAsset.width(this.data),
                            height: VistaAsset.height(this.data),
                            format: ((_f = (_e = (_d = this.data) === null || _d === void 0 ? void 0 : _d.info) === null || _e === void 0 ? void 0 : _e.image) === null || _f === void 0 ? void 0 : _f.format) || 'unknown',
                            isError: false,
                            // isSupported is unknown here. Hence purposely set it to false
                            isSupported: false,
                        };
                        if (this.data) {
                            // Here we are setting image info for UMS asset
                            this.setAssetData(Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, (_g = this.data) === null || _g === void 0 ? void 0 : _g.info), { image: imageInfoUMS }) }));
                        }
                    }
                }
                if ((_h = this.data) === null || _h === void 0 ? void 0 : _h.id) {
                    //this to make change backward compatible,
                    //if callback function is provided we will call (after image info is created in Sherbert Side), otherwise, ignore
                    if (callbackFunc)
                        callbackFunc(this.data.id);
                }
                // Don't set image info if the request fails, but don't throw an error either
                // eslint-disable-next-line no-empty
            }
            catch (_j) { }
        }));
    }
    get data() {
        return this._data;
    }
    setAssetData(assetData) {
        this._data = assetData;
    }
    get print() {
        return new VistaAsset(this._data, AssetType.Print, this.authProvider, this._signature, this._callbackFunc);
    }
    get webPreview() {
        return new VistaAsset(this._data, AssetType.WebPreview, this.authProvider, this._signature, this._callbackFunc);
    }
    get thumbnail() {
        return new VistaAsset(this._data, AssetType.Thumb, this.authProvider, this._signature, this._callbackFunc);
    }
    get embroidery() {
        return new VistaAsset(this._data, AssetType.Embroidery, this.authProvider, this._signature, this._callbackFunc);
    }
    get monochrome() {
        return new VistaAsset(this._data, AssetType.Monochrome, this.authProvider, this._signature, this._callbackFunc);
    }
    get sharpened() {
        return new VistaAsset(this._data, AssetType.Sharpened, this.authProvider, this._signature, this._callbackFunc);
    }
    get imageInfo() {
        return new VistaAsset(this._data, AssetType.ImageInfo, this.authProvider, this._signature, this._callbackFunc);
    }
    get isPresigned() {
        return !this.isDamAsset() || (this.signature !== undefined && this.signature !== '');
    }
    get signature() {
        // Using _signature property here to avoid an infinite loop
        // eslint-disable-next-line no-underscore-dangle
        return this._signature;
    }
    //Takes the any type because as of 2022-08-11 the info.image block
    //is no longer part of the Sherbert schema. UMS V2 assets endpoint
    //still puts information there for legacy uploads however. New DAM
    //based assets get their image info from the imageInfo variant.
    //This may change again in the near future so this logic may be short lived
    static width(data) {
        var _a, _b, _c, _d, _e;
        const format = (_b = (_a = data.info) === null || _a === void 0 ? void 0 : _a.image) === null || _b === void 0 ? void 0 : _b.format;
        return (_e = (_d = (_c = data.info) === null || _c === void 0 ? void 0 : _c.image) === null || _d === void 0 ? void 0 : _d[format]) === null || _e === void 0 ? void 0 : _e.width;
    }
    static height(data) {
        var _a, _b, _c, _d, _e;
        const format = (_b = (_a = data.info) === null || _a === void 0 ? void 0 : _a.image) === null || _b === void 0 ? void 0 : _b.format;
        return (_e = (_d = (_c = data.info) === null || _c === void 0 ? void 0 : _c.image) === null || _d === void 0 ? void 0 : _d[format]) === null || _e === void 0 ? void 0 : _e.height;
    }
    setSignature(signature) {
        this._signature = signature;
    }
    setStatus(status) {
        this.status = status;
    }
    setErrorStatus(error) {
        if (error instanceof Error) {
            this.setStatus({ type: AssetStatusType.Error, rawError: error });
        }
        else if (typeof error === 'string') {
            this.setStatus({ type: AssetStatusType.Error, rawError: new Error(error) });
        }
        else {
            this.setStatus({ type: AssetStatusType.Error, rawError: new Error('Unknown error') });
        }
    }
    /**
     * Prepares the asset for use by creating or retrieving asset data
     */
    prepare() {
        return __awaiter(this, void 0, void 0, function* () {
            this.setStatus({ type: AssetStatusType.Prepared });
            return Promise.resolve();
        });
    }
    updateProgress(percent) {
        this.setStatus({ type: AssetStatusType.Preparing, uploadProgress: percent });
    }
    /**
     * Presigns this asset by setting its signature to a value with presigned authorization. By default this presigning does not expire.
     * @returns {Promise<void>} Returns an empty Promise object.
     */
    presign() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isDamAsset()) {
                this.setSignature('abc');
                yield this.prepare();
            }
            if (this.signature ||
                this.status.type === AssetStatusType.Deleted ||
                this.status.type === AssetStatusType.Deleting) {
                return Promise.resolve();
            }
            let sig;
            try {
                const headers = new Headers(yield this.authProvider.getAuthHeaders());
                // By default, the signature returned here never expires.
                const result = (yield presignAsset({ path: { id: this._data.id } }, { headers }));
                if ((result === null || result === void 0 ? void 0 : result.status) === 200) {
                    sig = result.data.signature;
                }
                this.setSignature(sig);
                yield this.prepare();
            }
            catch (error) {
                throw new Error(AssetErrorMessage.PresignFailed);
            }
            if (!sig) {
                throw new Error(AssetErrorMessage.EmptySignature);
            }
            return;
        });
    }
    /**
     * cerate variant and return metadata of the asset.
     */
    getVariantMetadata(assetData, variant) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { data: variantAssetData } = yield getVariantMetadataWithPolling({ path: { id: assetData.id, variant } }, { headers: new Headers(yield this.authProvider.getAuthHeaders()) });
                return variantAssetData;
            }
            catch (error) {
                throw new Error('unable to create variant');
            }
        });
    }
    /**
     * Return print variant of the asset.
     */
    getPrint() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getVariantMetadata(this._data, VariantType.Print);
            const printAsset = new VistaAsset(data, AssetType.Print, this.authProvider, undefined, this._callbackFunc);
            yield printAsset.presign();
            return printAsset;
        });
    }
    /**
     * Return webPreview variant of the asset.
     */
    getWebPreview() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getVariantMetadata(this._data, VariantType.WebPreview);
            const previewAsset = new VistaAsset(data, AssetType.WebPreview, this.authProvider, undefined, this._callbackFunc);
            yield previewAsset.presign();
            return previewAsset;
        });
    }
    /**
     * Return thumb variant of the asset.
     */
    getThumb() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getVariantMetadata(this._data, VariantType.Thumb);
            const thumbAsset = new VistaAsset(data, AssetType.Thumb, this.authProvider, undefined, this._callbackFunc);
            yield thumbAsset.presign();
            return thumbAsset;
        });
    }
    /**
     * Return sharpened variant of the asset.
     */
    getSharpened() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getVariantMetadata(this._data, VariantType.Sharpened);
            const sharpenedAsset = new VistaAsset(data, AssetType.Sharpened, this.authProvider, undefined, this._callbackFunc);
            yield sharpenedAsset.presign();
            return sharpenedAsset;
        });
    }
    /**
     * Return background removed variant of the asset.
     */
    getBackgroundRemoved() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getVariantMetadata(this._data, VariantType.BackgroundRemoved);
            const backgroundRemovedAsset = new VistaAsset(data, AssetType.BackgroundKnockout, this.authProvider, undefined, this._callbackFunc);
            yield backgroundRemovedAsset.presign();
            return backgroundRemovedAsset;
        });
    }
    /**
     * Return Monochrome variant of the asset.
     */
    getMonochrome() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getVariantMetadata(this._data, VariantType.Monochrome);
            const monochromeAsset = new VistaAsset(data, AssetType.Monochrome, this.authProvider, undefined, this._callbackFunc);
            yield monochromeAsset.presign();
            return monochromeAsset;
        });
    }
    /**
     * Return Embroidery variant of the asset.
     */
    getEmbroidery() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getVariantMetadata(this._data, VariantType.Embroidery);
            const embroideryAsset = new VistaAsset(data, AssetType.Embroidery, this.authProvider, undefined, this._callbackFunc);
            yield embroideryAsset.presign();
            return embroideryAsset;
        });
    }
    /**
     * Hides the asset.
     * @param {Object} signal Object containing an optional abort signal property which is used to cancel method execution in progress.
     * @returns {Promise<Boolean>} Returns true if hide is successful else false.
     */
    hide({ signal, ownerId } = {}) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.isDamAsset()) {
                    const headers = new Headers(yield this.authProvider.getAuthHeaders());
                    yield hideAsset({ path: { id: this._data.id } }, { signal: signal, headers });
                    if ((_a = this.data) === null || _a === void 0 ? void 0 : _a.info.asset) {
                        this.setAssetData(Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, this.data.info), { asset: Object.assign(Object.assign({}, this.data.info.asset), { hidden: true }) }) }));
                        return true;
                    }
                }
                else {
                    const headers = yield this.authProvider.getAuthHeaders();
                    yield hideUnhide({
                        uploadId: this._data.id,
                        hide: true,
                        headers: headers,
                        signal: signal,
                        ownerId: ownerId,
                    });
                    if ((_b = this.data) === null || _b === void 0 ? void 0 : _b.info.asset) {
                        this.setAssetData(assetDataFromUMS(Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, this.data.info), { asset: Object.assign(Object.assign({}, this.data.info.asset), { hidden: true }) }) })));
                        return true;
                    }
                }
                return false;
            }
            catch (error) {
                throw error.message;
            }
        });
    }
    /**
     * Unhide the asset.
     * @param {Object} signal Object containing an optional abort signal property which is used to cancel method execution in progress.
     * @returns {Promise<Boolean>} Returns true if unhide is successful else false.
     */
    unhide({ signal, ownerId } = {}) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.isDamAsset()) {
                    const headers = new Headers(yield this.authProvider.getAuthHeaders());
                    yield unhideAsset({ path: { id: this._data.id } }, { signal: signal, headers });
                    if ((_a = this.data) === null || _a === void 0 ? void 0 : _a.info.asset) {
                        this.setAssetData(Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, this.data.info), { asset: Object.assign(Object.assign({}, this.data.info.asset), { hidden: false }) }) }));
                        return true;
                    }
                }
                else {
                    const headers = yield this.authProvider.getAuthHeaders();
                    yield hideUnhide({
                        uploadId: this._data.id,
                        hide: false,
                        headers: headers,
                        signal: signal,
                        ownerId: ownerId,
                    });
                    if ((_b = this.data) === null || _b === void 0 ? void 0 : _b.info.asset) {
                        this.setAssetData(assetDataFromUMS(Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, this.data.info), { asset: Object.assign(Object.assign({}, this.data.info.asset), { hidden: false }) }) })));
                        return true;
                    }
                }
                return false;
            }
            catch (error) {
                throw error.message;
            }
        });
    }
    /**
     * Deletes this asset.
     * @param {Object} signal Object containing an optional abort signal property which is used to cancel method execution in progress.
     * @returns {Promise<void>} Returns an empty Promise object.
     */
    delete({ signal, ownerId } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            // AbortSingle is not used in deleteAsset() method of asset-sdk
            if (this.status.type === AssetStatusType.Deleting) {
                yield when(() => this.status.type !== AssetStatusType.Deleting);
                return;
            }
            if (this.status.type !== AssetStatusType.Deleted && this.status.type !== AssetStatusType.Unprepared) {
                try {
                    this.setStatus({ type: AssetStatusType.Deleting, isHidden: true });
                    const headers = yield this.authProvider.getAuthHeaders();
                    if (this.isDamAsset()) {
                        yield deleteAsset({ path: { id: this._data.id } }, { headers: new Headers(headers) });
                    }
                    else {
                        yield deleteUpload({
                            headers,
                            uploadId: this._data.id,
                            ownerId,
                            signal,
                        });
                    }
                    this.setStatus({ type: AssetStatusType.Deleted, isHidden: true });
                }
                catch (error) {
                    this.setErrorStatus(error);
                    return;
                }
            }
        });
    }
    /**
     * Get the Page info of the specified page
     * @param {number} pageNo denotes the page no of which metadata is required
     * @param {Object} signal Object containing an optional abort signal property which is used to cancel method execution in progress.
     * @returns {Promise<ImageMetadata>} Returns metadata of the page specified
     */
    getPageImageInfo({ pageNo, signal }) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const imageMetadata = this._pageImageInfo[pageNo];
            if (imageMetadata !== undefined && imageMetadata !== null && Object.keys(imageMetadata).length !== 0) {
                return imageMetadata;
            }
            if (pageNo < 1) {
                throw new Error(`${pageNo} is not a valid page number`);
            }
            if (((_a = this._data.info.image) === null || _a === void 0 ? void 0 : _a.format) === 'pdf' &&
                ((_b = this._data.info.image) === null || _b === void 0 ? void 0 : _b.numPages) &&
                ((_c = this._data.info.image) === null || _c === void 0 ? void 0 : _c.numPages) < pageNo) {
                throw new Error(`page number must in range: [1-${(_d = this._data.info.image) === null || _d === void 0 ? void 0 : _d.numPages}]`);
            }
            let imageInfo;
            if ((_e = this.data) === null || _e === void 0 ? void 0 : _e.id) {
                imageInfo = (yield getPageImageInfo({ path: { id: this.data.id, pageNum: pageNo } }, { headers: new Headers(yield this.authProvider.getAuthHeaders()), signal: signal }));
            }
            else {
                throw new Error('Image info not found');
            }
            this._pageImageInfo[pageNo] = imageInfo.data;
            return imageInfo.data;
        });
    }
    /**
     * Gets the URL for the asset of the appropriate AssetType value, for example Print, Preview, or Original variants.
     * @param {GetUrlParams} ParametersObject used to pass parameters into this function. See type documentation for more detailed information.
     * This indicates whether or not to include the signature from the original so that presign doesn't
     * need to be called seperately.
     * @returns {string} Returns the requested asset URL.
     */
    getUrl({ includeSignature = true, showDeleted = false, pageNum } = {}) {
        switch (this.assetType) {
            case AssetType.Original:
                return this.getVariantPath('original', includeSignature, showDeleted, pageNum);
            case AssetType.Print:
                return this.getVariantPath('print', includeSignature, showDeleted, pageNum);
            case AssetType.WebPreview:
                return this.isDamAsset()
                    ? this.getVariantPath('webPreview', includeSignature, showDeleted, pageNum)
                    : this.getVariantPath('preview', includeSignature, showDeleted, pageNum);
            case AssetType.Thumb:
                return this.getVariantPath('thumb', includeSignature, showDeleted, pageNum);
            case AssetType.Embroidery:
                return this.getVariantPath('embroidery', includeSignature, showDeleted, pageNum);
            case AssetType.BackgroundKnockout:
                return this.getVariantPath('backgroundRemoved', includeSignature, showDeleted, pageNum);
            case AssetType.Sharpened:
                return this.getVariantPath('sharpened', includeSignature, showDeleted, pageNum);
            case AssetType.Monochrome:
                return this.getVariantPath('monochrome', includeSignature, showDeleted, pageNum);
            case AssetType.ImageInfo:
                return this.getVariantPath('imageInfo', includeSignature, showDeleted, pageNum);
            default:
                throw Error(`Unknown asset type --> ${this.assetType}`);
        }
    }
    getVariantPath(variantType, includeSignature, showDeleted, pageNum) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        // the double replace is to support both legacy uploads and uploads v1
        const url = new URL(this.getUrlInternal());
        if ((this._data.info.image === null || this._data.info.image === undefined) &&
            pageNum !== null &&
            pageNum !== undefined) {
            throw new Error('MultiPage variant generation is not applicable');
        }
        if (this.isDamAsset() && includeSignature) {
            url.searchParams.set('signature', this.signature ? this.signature : '');
        }
        if (((_a = this._data.info.image) === null || _a === void 0 ? void 0 : _a.format) !== 'pdf' && pageNum !== null && pageNum !== undefined && pageNum !== 1) {
            throw new Error(`MultiPage variant generation is not applicable for page number: ${pageNum}`);
        }
        if (!this.isDamAsset() && pageNum !== null && pageNum !== undefined && pageNum !== 1) {
            throw new Error('Page wise variant url for UMS is not applicable.');
        }
        if (pageNum !== null &&
            pageNum !== undefined &&
            ((_b = this._data.info.image) === null || _b === void 0 ? void 0 : _b.format) === 'pdf' &&
            variantType !== VariantType.WebPreview &&
            variantType !== VariantType.Preview &&
            variantType !== VariantType.Thumb) {
            throw new Error(`MultiPage variant generation is not applicable for: ${variantType}`);
        }
        let pathName = url.pathname;
        if (this.isDamAsset()) {
            // checking the asset is variant asset or not if asset is variant asset then asset.<variantType>.getUrl() will return variantAssetUrl/<variantType>.
            // if asset is not a variant asset then asset.<variantType>.getUrl() will return originalAssetUrl/<variantType>.
            if ((variantType !== 'original' && !((_d = (_c = this.data) === null || _c === void 0 ? void 0 : _c.info.asset) === null || _d === void 0 ? void 0 : _d.isVariant)) ||
                (((_f = (_e = this.data) === null || _e === void 0 ? void 0 : _e.info.asset) === null || _f === void 0 ? void 0 : _f.isVariant) && variantType !== ((_h = (_g = this.data) === null || _g === void 0 ? void 0 : _g.info.asset) === null || _h === void 0 ? void 0 : _h.variant))) {
                pathName += `/${variantType}`;
            }
        }
        else {
            pathName = url.pathname.replace('original', variantType).replace('print', variantType);
        }
        if (((_j = this._data.info.image) === null || _j === void 0 ? void 0 : _j.format) === 'pdf') {
            if (variantType !== 'original' && pageNum !== undefined && this._data.info.image.numPages) {
                if (pageNum >= 1 && pageNum <= this._data.info.image.numPages) {
                    pathName += `/${pageNum}`;
                }
                else {
                    throw new Error(`page number must be in range: [1-${this._data.info.image.numPages}]`);
                }
            }
        }
        url.pathname = pathName;
        const pathUrl = url.toString() + `&showDeleted=${showDeleted}`;
        return pathUrl;
    }
    getUrlInternal() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        let url = '';
        if (this.isDamAsset()) {
            url = SHERBERT_URL;
        }
        if ((_b = (_a = this.data) === null || _a === void 0 ? void 0 : _a.info.asset) === null || _b === void 0 ? void 0 : _b.isVariant) {
            const id = this._data.id;
            return `${SHERBERT_BASE_URL}/${id}`;
        }
        if ((_d = (_c = this._data) === null || _c === void 0 ? void 0 : _c.properties) === null || _d === void 0 ? void 0 : _d.url) {
            url = (_e = this._data) === null || _e === void 0 ? void 0 : _e.properties.url;
        }
        else if (((_h = (_g = (_f = this._data) === null || _f === void 0 ? void 0 : _f._links) === null || _g === void 0 ? void 0 : _g.self) === null || _h === void 0 ? void 0 : _h.href) !== undefined && ((_l = (_k = (_j = this._data) === null || _j === void 0 ? void 0 : _j._links) === null || _k === void 0 ? void 0 : _k.self) === null || _l === void 0 ? void 0 : _l.href) !== null) {
            if ((_p = (_o = (_m = this._data) === null || _m === void 0 ? void 0 : _m._links) === null || _o === void 0 ? void 0 : _o.self) === null || _p === void 0 ? void 0 : _p.href.toLowerCase().startsWith(SHERBERT_BASE_URL)) {
                const assetId = fetchAssetIdFromSherbertUrl((_s = (_r = (_q = this._data) === null || _q === void 0 ? void 0 : _q._links) === null || _r === void 0 ? void 0 : _r.self) === null || _s === void 0 ? void 0 : _s.href);
                return `${SHERBERT_BASE_URL}/${assetId}`;
            }
            url = this._data._links.self.href;
        }
        return url;
    }
    isDamAsset() {
        return (this._data.tenant === Tenants.VistaPrint ||
            this._data.tenant === Tenants.VistaCreate ||
            this._data.tenant === Tenants.Vista99D ||
            this._data.tenant === Tenants.VistaNonProd);
    }
}
__decorate([
    observable
], VistaAsset.prototype, "_data", void 0);
__decorate([
    observable
], VistaAsset.prototype, "status", void 0);
__decorate([
    observable
], VistaAsset.prototype, "_signature", void 0);
__decorate([
    observable
], VistaAsset.prototype, "_callbackFunc", void 0);
__decorate([
    computed
], VistaAsset.prototype, "data", null);
__decorate([
    action.bound
], VistaAsset.prototype, "setAssetData", null);
__decorate([
    computed
], VistaAsset.prototype, "print", null);
__decorate([
    computed
], VistaAsset.prototype, "webPreview", null);
__decorate([
    computed
], VistaAsset.prototype, "thumbnail", null);
__decorate([
    computed
], VistaAsset.prototype, "embroidery", null);
__decorate([
    computed
], VistaAsset.prototype, "monochrome", null);
__decorate([
    computed
], VistaAsset.prototype, "sharpened", null);
__decorate([
    computed
], VistaAsset.prototype, "imageInfo", null);
__decorate([
    computed
], VistaAsset.prototype, "isPresigned", null);
__decorate([
    computed
], VistaAsset.prototype, "signature", null);
__decorate([
    action.bound
], VistaAsset.prototype, "setSignature", null);
__decorate([
    action.bound
], VistaAsset.prototype, "setStatus", null);
//# sourceMappingURL=VistaAsset.js.map